import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { X, ThumbsUp, LogOut } from "lucide-react";
import confetti from "canvas-confetti";
import type { EventRsvpItem } from "../lib/api/useEventRsvp";
import type { BuddyRequestItem } from "../lib/api/useEventBuddies";
import { ProfileImage } from "./ProfileImage";
import { BuddyAvatar } from "./BuddyAvatar";
import { useBackButtonClose } from "../lib/useBackButtonClose";

interface Props {
  isOpen: boolean;
  celebrate: boolean;
  onClose: () => void;
  onLeave: () => void;
  onTeamup: () => void;
  onBuddyClick: (id: string) => void;
  rsvpItems: EventRsvpItem[];
  buddyItems: BuddyRequestItem[];
}

export function EventRsvpSheet({
  isOpen,
  celebrate,
  onClose,
  onLeave,
  onTeamup,
  onBuddyClick,
  rsvpItems,
  buddyItems,
}: Props) {
  useBackButtonClose(isOpen, onClose);

  useEffect(() => {
    if (!isOpen || !celebrate) return;
    const colors = ["#10b981", "#fbbf24", "#f472b6", "#818cf8"];
    confetti({ particleCount: 160, spread: 120, origin: { y: 0.9 }, colors, zIndex: 9999 });
  }, [isOpen, celebrate]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[45]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />

          <div className="fixed left-0 right-0 z-[46] above-rsvp-bar flex justify-center px-4 pointer-events-none">
            <motion.div
              className="pointer-events-auto w-full max-w-sm rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-black/8 dark:border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.18)]"
              initial={{ opacity: 0, y: 52, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 28, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 420, damping: 30 }}
            >
              {/* Header */}
              <div className="relative flex flex-col items-center pt-6 pb-5 px-5 border-b border-black/10 dark:border-white/10 text-center">
                <button
                  onClick={onClose}
                  className="absolute top-3.5 right-3.5 p-1.5 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>

                {celebrate ? (
                  <motion.div
                    initial={{ scale: 0.3, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 380, damping: 16, delay: 0.08 }}
                    className="text-5xl mb-3 select-none"
                  >
                    🎉
                  </motion.div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-emerald-500/12 dark:bg-emerald-500/18 flex items-center justify-center mb-3">
                    <ThumbsUp className="w-6 h-6 text-emerald-500" />
                  </div>
                )}

                <motion.h2
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: celebrate ? 0.18 : 0.04 }}
                  className="text-xl font-bold text-neutral-900 dark:text-neutral-100"
                >
                  {celebrate ? "You're going! 🎊" : "You're going"}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: celebrate ? 0.26 : 0.08 }}
                  className="text-sm text-neutral-500 dark:text-neutral-400 mt-1"
                >
                  {celebrate ? "Can't wait to see you there" : "See you at the event"}
                </motion.p>
              </div>

              {/* Who's going */}
              {rsvpItems.length > 0 && (
                <div className="px-5 py-4 border-b border-black/10 dark:border-white/10">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
                    Who's going · {rsvpItems.length}
                  </p>
                  <div className="flex items-start gap-4 overflow-x-auto pb-1 -mx-5 px-5">
                    {rsvpItems.slice(0, 16).map((r) => (
                      <div key={r.id} className="shrink-0 flex flex-col items-center gap-1.5">
                        <div className="h-14 flex items-center justify-center">
                          <ProfileImage
                            size="md"
                            user={{ firstName: r.firstName, lastName: r.lastName, profileImageUrl: r.profileImageUrl }}
                          />
                        </div>
                        <span className="text-[11px] text-neutral-600 dark:text-neutral-400 font-medium w-16 text-center leading-tight truncate">
                          {r.firstName}
                        </span>
                      </div>
                    ))}
                    {rsvpItems.length > 16 && (
                      <div className="shrink-0 flex flex-col items-center gap-1.5">
                        <div className="h-14 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
                            +{rsvpItems.length - 16}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Team Up / Kiki */}
              <div className="px-5 py-4 border-b border-black/10 dark:border-white/10">
                <div className="flex items-center gap-1.5 mb-1">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                    Let's have a kiki
                  </p>
                  <button
                    type="button"
                    onClick={onTeamup}
                    className="ml-auto text-xs font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    + Find others
                  </button>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                  Connect with others going to this event and go together.
                </p>
                {buddyItems.length > 0 ? (
                  <div className="flex items-start gap-4 overflow-x-auto pb-1 -mx-5 px-5">
                    {buddyItems.slice(0, 8).map((b) => (
                      <BuddyAvatar key={b.id} buddy={b} onClick={() => onBuddyClick(b.id)} />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 italic">
                    No one yet — be the first!
                  </p>
                )}
              </div>

              {/* Leave */}
              <div className="px-5 py-3 flex justify-center">
                <button
                  type="button"
                  onClick={onLeave}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-400 dark:text-neutral-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <LogOut className="w-3 h-3" />
                  Leave event
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
